/**
 * Fetching a file (PDF, CSV) from an AUTHED endpoint, safely.
 *
 * A bare `<a download>` or `window.location.assign` saves whatever comes
 * back under a name the browser invents — the JSON `{ message }` body when
 * the render is still running (202), the session has expired (401) or the
 * invoice's lines do not reconcile (409) — and, worse, it sends no
 * kiosk/impersonation headers, so a staffer in "Access school view" fetches
 * as themselves and is refused. So a download fetches first, accepts ONLY a
 * 200 whose body is the expected type, and reports every other answer with
 * the server's own message so the toast can say why.
 *
 * DOM-free on purpose: the object-URL anchor click is `saveBlob` in
 * `api/http.ts`, which the caller runs on the result. The fetch is
 * injectable so the status handling is unit-testable without a browser.
 */

/** The slice of `fetch` this module needs. */
export type PdfFetch = (
  url: string,
  init: { credentials: 'include'; headers: Record<string, string> },
) => Promise<{
  status: number
  headers: { get(name: string): string | null }
  blob(): Promise<Blob>
  text(): Promise<string>
}>

export type AuthedPdfResult =
  | { ok: true; blob: Blob; filename: string }
  | { ok: false; status: number; message: string }

export const PDF_DOWNLOAD_FAILED_MESSAGE = 'Unable to download the PDF — try again'
export const FILE_DOWNLOAD_FAILED_MESSAGE = 'Unable to download the file — try again'

/** `attachment; filename="invoice-summary_2026-06-01-2026-06-30.pdf"` → the name, else the fallback. */
export function filenameFromContentDisposition(header: string | null, fallback: string): string {
  if (!header) {
    return fallback
  }
  const match = /filename\*?=(?:"([^"]+)"|([^;]+))/i.exec(header)
  const raw = (match?.[1] ?? match?.[2])?.trim()
  return raw && raw.length > 0 ? raw : fallback
}

/** The server's own `{ message }`, if the non-PDF body happens to carry one. */
async function serverMessage(response: Awaited<ReturnType<PdfFetch>>): Promise<string | null> {
  try {
    const parsed = JSON.parse(await response.text()) as { message?: unknown }
    return typeof parsed.message === 'string' && parsed.message.trim().length > 0 ? parsed.message : null
  } catch {
    return null
  }
}

export interface AuthedFileOptions {
  /** The content type the caller is willing to save (`application/pdf`, `text/csv`). */
  accept: string
  fallbackFilename: string
  /** Shown when the server gives no `{ message }` of its own. */
  failedMessage?: string
  /**
   * The kiosk / impersonation tokens (`authHeaders()` in `api/http.ts`) —
   * they live in storage, not cookies, so a request without them downloads
   * as the real user instead of the impersonated school.
   */
  headers?: Record<string, string>
  fetchImpl?: PdfFetch
}

/**
 * Fetch one file. Never throws: a network failure is `{ ok: false, status: 0 }`.
 */
export async function fetchAuthedFile(url: string, options: AuthedFileOptions): Promise<AuthedPdfResult> {
  const failed = options.failedMessage ?? FILE_DOWNLOAD_FAILED_MESSAGE
  let response: Awaited<ReturnType<PdfFetch>>
  try {
    response = await (options.fetchImpl ?? fetch)(url, { credentials: 'include', headers: options.headers ?? {} })
  } catch {
    return { ok: false, status: 0, message: failed }
  }
  // A 202 "still rendering" is `ok` to fetch(), and its JSON body is not a
  // PDF; neither is the SPA shell a stale service worker answers with a 200.
  const contentType = (response.headers.get('content-type') ?? '').toLowerCase()
  if (response.status !== 200 || !contentType.includes(options.accept.toLowerCase())) {
    return {
      ok: false,
      status: response.status,
      message: (await serverMessage(response)) ?? failed,
    }
  }
  return {
    ok: true,
    blob: await response.blob(),
    filename: filenameFromContentDisposition(response.headers.get('content-disposition'), options.fallbackFilename),
  }
}

/** `fetchAuthedFile` for a PDF. */
export function fetchAuthedPdf(
  url: string,
  options: { fallbackFilename: string; headers?: Record<string, string>; fetchImpl?: PdfFetch },
): Promise<AuthedPdfResult> {
  return fetchAuthedFile(url, { ...options, accept: 'application/pdf', failedMessage: PDF_DOWNLOAD_FAILED_MESSAGE })
}

/** `fetchAuthedFile` for a CSV. */
export function fetchAuthedCsv(
  url: string,
  options: { fallbackFilename: string; headers?: Record<string, string>; fetchImpl?: PdfFetch },
): Promise<AuthedPdfResult> {
  return fetchAuthedFile(url, { ...options, accept: 'text/csv' })
}
